import { lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { AuthCallback } from "./components/AuthCallback";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";

// Pages — core storefront eager, everything else lazy (code splitting)
import HomePage from "./pages/HomePage";
import ShopsPage from "./pages/ShopsPage";
import ShopDetailPage from "./pages/ShopDetailPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CatalogPage from "./pages/CatalogPage";
import CartPage from "./pages/CartPage";
import ResumeOrderPage from "./pages/ResumeOrderPage";
import ConsentPage from "./pages/ConsentPage";
import PublicProposalPage from "./pages/PublicProposalPage";
import PerMePage from "./pages/PerMePage";
import { PER_ME_ENABLED } from "./lib/perMe";
import SubscriptionManagePage, { SubscriptionSuccessPage } from "./pages/SubscriptionPages";
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const CheckoutSuccessPage = lazy(() => import("./pages/CheckoutSuccessPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const MyOrdersPage = lazy(() => import("./pages/MyOrdersPage"));
const RicorrenzePage = lazy(() => import("./pages/RicorrenzePage"));
const FloristOnboarding = lazy(() => import("./pages/FloristOnboarding"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const CustomerDashboard = lazy(() => import("./pages/CustomerDashboard"));
const FloristLandingPage = lazy(() => import("./pages/FloristLandingPage"));
const GuidedPurchase = lazy(() => import("./pages/GuidedPurchase"));
const ReorderPage = lazy(() => import("./pages/ReorderPage"));
const SeoLandingPage = lazy(() => import("./pages/SeoLandingPage"));
const CityLandingPage = lazy(() => import("./pages/CityLandingPage"));

const LegacyCityRedirect = () => { const { citySlug } = useParams(); return <Navigate to={`/fiori-a-${citySlug}`} replace />; };
const SlugDispatcher = () => {
  const { slug } = useParams();
  if (slug.startsWith("fiori-a-") && slug !== "fiori-a-domicilio") return <CityLandingPage key={slug} slug={slug.slice(8)} />;
  return <SeoLandingPage />;
};
const SiteMapPage = lazy(() => import("./pages/SiteMapPage"));
const MomentPage = lazy(() => import("./pages/MomentPage"));
const TrackOrderPage = lazy(() => import("./pages/TrackOrderPage"));
const ReviewPage = lazy(() => import("./pages/ReviewPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const ForgotPasswordPage = lazy(() => import("./pages/PasswordResetPages").then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import("./pages/PasswordResetPages").then(m => ({ default: m.ResetPasswordPage })));
import { HelmetProvider } from "react-helmet-async";

// Layout
import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import { ScrollToTop } from "./components/ScrollToTop";

const LegacyShopRedirect = () => {
  const { subdomain } = useParams();
  return <Navigate to={`/fioristi/${subdomain}`} replace />;
};

function AppRoutes() {
  const location = useLocation(); // read the fragment from here (reactive), not window.location.hash
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={<HomePage />} />
              <Route path="login" element={<LoginPage />} />
              <Route path="register" element={<RegisterPage />} />
              <Route path="apri-negozio" element={<FloristOnboarding />} />
              <Route path="fioristi" element={<FloristLandingPage />} />
              <Route path="diventa-fiorista" element={<FloristLandingPage />} />
              <Route path="trova-il-gesto-giusto" element={<GuidedPurchase />} />
              <Route path="shops" element={<ShopsPage />} />
              <Route path="shops/:shopId" element={<ShopDetailPage />} />
              <Route path="fioristi/:subdomain" element={<ShopDetailPage />} />
              <Route path="s/:subdomain" element={<LegacyShopRedirect />} />
              <Route path="products/:productId" element={<ProductDetailPage />} />
              <Route path="cart" element={<CartPage />} />
              <Route path="checkout" element={<CheckoutPage />} />
              <Route path="checkout/success" element={<CheckoutSuccessPage />} />
              <Route path="riordina/:orderId" element={<ReorderPage />} />
              <Route path="traccia/:orderId" element={<TrackOrderPage />} />
              <Route path="riprendi/:orderId" element={<ResumeOrderPage />} />
              <Route path="consenso/:token" element={<ConsentPage />} />
              <Route path="proposta/:proposalId" element={<PublicProposalPage />} />
              <Route path="fiori-per-me" element={PER_ME_ENABLED ? <PerMePage /> : <Navigate to="/" replace />} />
              <Route path="abbonamento/successo" element={<SubscriptionSuccessPage />} />
              <Route path="abbonamento/:subId" element={<SubscriptionManagePage />} />
              <Route path="recensione/:orderId" element={<ReviewPage />} />
              <Route path="catalogo" element={<CatalogPage />} />
              <Route path="privacy" element={<LegalPage />} />
              <Route path="privacy/:slug" element={<LegalPage />} />
              <Route path="password-dimenticata" element={<ForgotPasswordPage />} />
              <Route path="reimposta-password" element={<ResetPasswordPage />} />
              <Route path="momenti/:momentId" element={<MomentPage />} />
              <Route path="checkout/cancel" element={<CartPage />} />
              <Route path="blog" element={<BlogPage />} />
              <Route path="blog/:postId" element={<BlogPostPage />} />
              <Route path="admin" element={
                <ProtectedRoute requiredRole="admin">
                  <AdminPage />
                </ProtectedRoute>
              } />
              <Route path="my-orders" element={
                <ProtectedRoute>
                  <CustomerDashboard />
                </ProtectedRoute>
              } />
              <Route path="account" element={
                <ProtectedRoute>
                  <CustomerDashboard />
                </ProtectedRoute>
              } />
              <Route path="ricorrenze" element={
                <ProtectedRoute>
                  <RicorrenzePage />
                </ProtectedRoute>
              } />
              <Route path="dashboard/*" element={
                <ProtectedRoute requiredRole="florist">
                  <DashboardPage />
                </ProtectedRoute>
              } />
              <Route path="mappa-del-sito" element={<SiteMapPage />} />
              <Route path="fiori-a-domicilio/:citySlug" element={<LegacyCityRedirect />} />
              <Route path=":slug" element={<SlugDispatcher />} />
            </Route>
          </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
